/**
 * CORE-1479 — наградные реакции в выборе: открыта ли, под замком или не
 * показывается (правило — `utils/rewardReactionsAccess`). `guardAdd` ставят
 * перед постановкой реакции: закрытая не уходит на сервер, человек видит
 * подсказку. Снимать свою реакцию этим не проверяют — снять можно всегда.
 *
 * Кто сейчас и что у него с наградой, хук читает из лёгкого снимка
 * (`rewardReactionsBus`), который пишет AuthContext, — а не из самого
 * AuthContext: хук стоит в хабах комнаты и рядах реакций, и тянуть туда
 * контекст авторизации со всеми его сервисами нельзя. По той же причине
 * текст подсказки (i18n) подгружается только в момент, когда он нужен.
 */
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { getRewardReactionsSnapshot, subscribeRewardReactionsSnapshot } from '../utils/rewardReactionsBus';
import { rewardAccessOf, type RewardAccess } from '../utils/rewardReactionsAccess';

export function useRewardReactions() {
  const { signedIn, state } = useSyncExternalStore(
    subscribeRewardReactionsSnapshot,
    getRewardReactionsSnapshot,
    getRewardReactionsSnapshot,
  );

  const access = useCallback(
    (type: string): RewardAccess => rewardAccessOf(state, signedIn, type),
    [state, signedIn],
  );

  const guardAdd = useCallback((type: string): boolean => {
    if (access(type) === 'open') return true;
    void import('../utils/rewardReactions')
      .then((m) => m.notifyRewardLocked(state))
      .catch(() => { /* подсказка не загрузилась — реакция всё равно не ушла */ });
    return false;
  }, [access, state]);

  return useMemo(() => ({ access, guardAdd }), [access, guardAdd]);
}
